import type { ReactNode } from 'react'
import { Focusable } from 'react-aria-components'
import { NavLink } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { Tooltip } from '@/components/base/tooltip/tooltip'
import { cx } from '@/utils/cx'

/**
 * PRD-018 §3.4: title meta → actions → tab strip → body. Every object page uses these three.
 *
 * The actions row always sits beneath the facts, left-aligned to the content edge. It never floats right of
 * the title: the earlier `flex-wrap justify-between` layout put the buttons on the title line when the facts
 * were short and on their own row when they were long, so the same file page looked different on Overview
 * and Close (QA-14, Sean 2026-09-13). Facts wrap; they never clip.
 */
export function TitleMeta({ emblem, name, facts, actions }: { emblem: ReactNode, name: string, facts: ReactNode[], actions?: ReactNode }) {
  const shown = facts.filter(f => f !== '' && f !== null && f !== undefined && f !== false)
  // Facts are strings or keyed elements; the key for an element is its own key (page code sets one).
  const keyOf = (f: ReactNode, i: number) => typeof f === 'string' ? f : (typeof f === 'object' && f !== null && 'key' in f && f.key) ? String(f.key) : `fact-${i}`
  return (
    <div className="flex flex-col gap-3 px-8 pt-6" data-testid="title-meta">
      <div className="flex items-start gap-3">
        <span aria-hidden className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-sm font-semibold text-secondary">{emblem}</span>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-primary" data-testid="page-title">{name}</h1>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-tertiary" data-testid="title-facts">{shown.map((f, i) => <span key={keyOf(f, i)}>{f}</span>)}</p>
        </div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2" data-testid="title-actions">{actions}</div> : null}
    </div>
  )
}

/** `nested`: the tab stays current on child routes (Settings → Close templates covers `/firm/templates/:id`). */
export interface Tab { id: string, label: string, to: string, badge?: number, preview?: 'R1' | 'R2', nested?: boolean }

/**
 * Section links that change the URL, marked up as what they are (audit P2-2): a `nav` of links with
 * `aria-current="page"` on the active one, not a tablist without the tablist keyboard model.
 */
export function TabStrip({ tabs, label = 'Sections' }: { tabs: Tab[], label?: string }) {
  return (
    <nav aria-label={label} className="mt-4 flex gap-1 border-b border-secondary px-8" data-testid="tab-strip">
      {tabs.map(t => (
        <NavLink key={t.id} to={t.to} end={!t.nested} data-tab={t.id} className={({ isActive }) => cx('-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]', isActive ? 'border-brand text-brand-secondary' : 'border-transparent text-tertiary hover:text-primary')}>
          <span>{t.label}</span>
          {t.badge ? <span className="rounded-full px-1.5 text-xs" style={{ background: 'var(--shell-hot-bg)', color: 'var(--shell-hot-fg)' }}>{t.badge}</span> : null}
          {t.preview ? <span className="rounded bg-secondary px-1 text-[10px] font-semibold text-quaternary">{t.preview}</span> : null}
        </NavLink>
      ))}
    </nav>
  )
}

/** R1 controls rendered disabled with the reason (PRD-018 §3.4, §3.7). */
export function DisabledR1Action({ label }: { label: string }) {
  return (
    <Tooltip title="Arrives with R1 skill threads" placement="bottom">
      <Focusable>
        <span className="inline-flex" tabIndex={0} aria-label={`${label}: arrives with R1 skill threads`}>
          <Button color="secondary" size="sm" isDisabled data-testid={`r1-${label.toLowerCase().replace(/\s+/g, '-')}`}>{label}</Button>
        </span>
      </Focusable>
    </Tooltip>
  )
}

export function Body({ children }: { children: ReactNode }) {
  return <div className="grid min-w-[1180px] grid-cols-12 gap-6 px-8 py-6">{children}</div>
}

export function Block({ title, span = 12, children, footer, actions, testId }: { title: string, span?: number, children: ReactNode, footer?: ReactNode, actions?: ReactNode, testId?: string }) {
  return (
    <section className="rounded-xl border border-secondary bg-primary" style={{ gridColumn: `span ${span} / span ${span}` }} data-testid={testId}>
      <header className="flex items-center justify-between gap-3 border-b border-secondary px-5 py-3 text-sm font-semibold text-primary">
        <span>{title}</span>
        {actions ? <span className="flex items-center gap-2 font-normal">{actions}</span> : null}
      </header>
      <div className="p-5">{children}</div>
      {footer ? <footer className="border-t border-secondary px-5 py-3 text-sm">{footer}</footer> : null}
    </section>
  )
}

const SKELETON_WIDTHS = ['82%', '70%', '91%', '76%', '88%', '73%', '95%', '79%']

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div aria-busy className="flex flex-col gap-2">
      {SKELETON_WIDTHS.slice(0, rows).map(w => <div key={w} className="h-4 animate-pulse rounded bg-secondary" style={{ width: w }} />)}
    </div>
  )
}

/** Empty state in plain words with a next step where one exists (audit P2-12). */
export function Empty({ children, action }: { children: ReactNode, action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-tertiary" data-testid="empty">
      <p>{children}</p>
      {action ? <div>{action}</div> : null}
    </div>
  )
}

export function BlockError({ message, onRetry }: { message: string, onRetry: () => void }) {
  return (
    <div role="alert" className="flex items-center justify-between gap-3 text-sm text-error-primary">
      <span>{message}</span>
      <Button size="sm" color="secondary" onClick={onRetry}>Retry</Button>
    </div>
  )
}

/** PRD-018 §3.9 wording: `Closed · reopen requires an admin (CFO)`, with the path forward (the current open month setting, §3.17). */
export function ClosedBanner({ periodLabel, isCfo }: { periodLabel: string, isCfo: boolean }) {
  return (
    <div role="status" className="mx-8 mt-4 rounded-lg border border-warning bg-warning-secondary px-4 py-2 text-sm text-warning-primary" data-testid="closed-banner">
      <span className="font-medium">{`${periodLabel} · Closed · reopen requires an admin (CFO).`}</span>
      {' '}
      {isCfo ? 'Items here are read-only. Reopen an item from its drawer (Tier 2 code, reason recorded) or change the current open month under Settings → General.' : 'Items here are read-only. Ask a CFO to reopen the item you need, or add a note in the drawer.'}
    </div>
  )
}
